import React, { useRef, useState } from 'react';
import { FiEdit2, FiTrash2, FiX, FiCheck, FiPlus } from 'react-icons/fi';
import { Modal, Button, TextField } from '../../design-system';
import { updateWell, deleteWell, addWell } from '../../api/data';
import { Well } from '../../@types/wells/types';
import { useAppContext } from '../../providers/AppContext';
import styles from './ManageWellsModal.module.css';

type IconProps = { size?: number };
const EditIcon  = FiEdit2  as React.FC<IconProps>;
const TrashIcon = FiTrash2 as React.FC<IconProps>;
const XIcon     = FiX     as React.FC<IconProps>;
const CheckIcon = FiCheck as React.FC<IconProps>;
const PlusIcon  = FiPlus  as React.FC<IconProps>;

type RowState = 'idle' | 'editing' | 'deleting';

type EditForm = {
  name: string;
  color: string;
};

const toEditForm = (w: Well): EditForm => ({
  name:            w.well_name,
  color:           w.color ?? '#4ADE80',
});

type Props = {
  open: boolean;
  onClose: () => void;
  wells: Well[];
  projectUid: string | null;
  onWellsChange: (wells: Well[]) => void;
};

const EMPTY_NEW = { name: '', color: '#4ADE80' };

const ManageWellsModal = ({ open, onClose, wells, projectUid, onWellsChange }: Props) => {
  const { updateData, data } = useAppContext();
  const [rowState, setRowState]   = useState<Record<string, RowState>>({});
  const [editForms, setEditForms] = useState<Record<string, EditForm>>({});
  const [busy, setBusy]           = useState<Record<string, boolean>>({});

  const [isCreating, setIsCreating] = useState(false);
  const [newForm, setNewForm]       = useState(EMPTY_NEW);
  const [creating, setCreating]     = useState(false);
  const newNameRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;

  const getState   = (uid: string): RowState  => rowState[uid]  ?? 'idle';
  const getForm    = (uid: string): EditForm  => editForms[uid] ?? toEditForm(wells.find(w => w.uid === uid)!);
  const isBusy     = (uid: string)            => busy[uid] ?? false;

  const startEdit = (well: Well) => {
    setEditForms(f => ({ ...f, [well.uid]: toEditForm(well) }));
    setRowState(s => ({ ...s, [well.uid]: 'editing' }));
  };

  const cancelRow = (uid: string) => {
    setRowState(s => ({ ...s, [uid]: 'idle' }));
  };

  const patchForm = (uid: string, patch: Partial<EditForm>) => {
    setEditForms(f => ({ ...f, [uid]: { ...getForm(uid), ...patch } }));
  };

  const handleSave = async (well: Well) => {
    const form = getForm(well.uid);
    setBusy(b => ({ ...b, [well.uid]: true }));
    try {
      const updated = await updateWell({
        project_uid:      projectUid ?? undefined,
        well_uid:         well.uid,
        well_name:        form.name,
        color:            form.color,
      });
      const next = wells.map(w => w.uid === updated.uid ? updated : w);
      onWellsChange(next);
      updateData({ wells: (data.wells || []).map(w => w.uid === updated.uid ? updated : w) });
      setRowState(s => ({ ...s, [well.uid]: 'idle' }));
    } catch {
      // keep row open so user can retry
    } finally {
      setBusy(b => ({ ...b, [well.uid]: false }));
    }
  };

  const openCreate = () => {
    setNewForm(EMPTY_NEW);
    setIsCreating(true);
    setTimeout(() => newNameRef.current?.focus(), 50);
  };

  const cancelCreate = () => {
    setIsCreating(false);
    setNewForm(EMPTY_NEW);
  };

  const handleCreate = async () => {
    if (!newForm.name.trim() || !projectUid) return;
    setCreating(true);
    try {
      const created = await addWell({
        project_uid: projectUid,
        well_name:   newForm.name.trim(),
        color:       newForm.color,
        curves:      [],
      });
      const next = [...wells, created];
      onWellsChange(next);
      updateData({ wells: [...(data.wells || []), created] });
      setIsCreating(false);
      setNewForm(EMPTY_NEW);
    } catch {
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (uid: string) => {
    setBusy(b => ({ ...b, [uid]: true }));
    try {
      await deleteWell(uid);
      const next = wells.filter(w => w.uid !== uid);
      onWellsChange(next);
      updateData({ wells: (data.wells || []).filter(w => w.uid !== uid) });
      setRowState(s => { const c = { ...s }; delete c[uid]; return c; });
    } catch {
    } finally {
      setBusy(b => ({ ...b, [uid]: false }));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Manage Wells"
      size="sm"
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <div className={styles.list}>
        {wells.length === 0 && (
          <p className={styles.empty}>No wells found for this project.</p>
        )}
        {wells.map(well => {
          const state = getState(well.uid);
          const form  = getForm(well.uid);
          const loading = isBusy(well.uid);

          return (
            <div key={well.uid} className={styles.row}>
              {/* ── View row ── */}
              <div className={styles.rowHeader}>
                <span
                  className={styles.colorDot}
                  style={{ background: well.color ?? '#4ADE80' }}
                />
                <span className={styles.wellName}>{well.well_name}</span>

                {state === 'idle' && (
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      title="Edit well"
                      onClick={() => startEdit(well)}
                    >
                      <EditIcon size={13} />
                    </button>
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                      title="Delete well"
                      onClick={() => setRowState(s => ({ ...s, [well.uid]: 'deleting' }))}
                    >
                      <TrashIcon size={13} />
                    </button>
                  </div>
                )}

                {state === 'editing' && (
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      title="Cancel"
                      onClick={() => cancelRow(well.uid)}
                    >
                      <XIcon size={13} />
                    </button>
                  </div>
                )}

                {state === 'deleting' && (
                  <div className={styles.deleteConfirm}>
                    <span className={styles.deleteLabel}>Remove this well?</span>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      title="Cancel"
                      onClick={() => cancelRow(well.uid)}
                    >
                      <XIcon size={13} />
                    </button>
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                      title="Confirm delete"
                      disabled={loading}
                      onClick={() => handleDelete(well.uid)}
                    >
                      {loading ? <span className={styles.spinner} /> : <CheckIcon size={13} />}
                    </button>
                  </div>
                )}
              </div>

              {/* ── Edit form (expands below) ── */}
              {state === 'editing' && (
                <div className={styles.editForm}>
                  <div className={styles.editRow}>
                    <TextField
                      label="Well Name"
                      fullWidth
                      value={form.name}
                      onChange={e => patchForm(well.uid, { name: e.target.value })}
                    />
                    <div className={styles.editColorField}>
                      <label className={styles.fieldLabel}>Color</label>
                      <div className={styles.colorWrapper}>
                        <input
                          type="color"
                          className={styles.colorInput}
                          value={form.color}
                          onChange={e => patchForm(well.uid, { color: e.target.value })}
                        />
                        <span className={styles.colorHex}>{form.color.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.editActions}>
                    <Button variant="secondary" size="sm" onClick={() => cancelRow(well.uid)} disabled={loading}>
                      Cancel
                    </Button>
                    <Button variant="primary" size="sm" loading={loading} onClick={() => handleSave(well)}>
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* ── New well ghost input / create form ── */}
        {!isCreating ? (
          <button type="button" className={styles.ghostInput} onClick={openCreate}>
            <PlusIcon size={13} />
            <span>New well</span>
          </button>
        ) : (
          <div className={styles.createForm}>
            <div className={styles.editRow}>
              <TextField
                label="Well Name"
                fullWidth
                value={newForm.name}
                onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') cancelCreate(); }}
                ref={newNameRef}
              />
              <div className={styles.editColorField}>
                <label className={styles.fieldLabel}>Color</label>
                <div className={styles.colorWrapper}>
                  <input
                    type="color"
                    className={styles.colorInput}
                    value={newForm.color}
                    onChange={e => setNewForm(f => ({ ...f, color: e.target.value }))}
                  />
                  <span className={styles.colorHex}>{newForm.color.toUpperCase()}</span>
                </div>
              </div>
            </div>

            <div className={styles.editActions}>
              <Button variant="secondary" size="sm" onClick={cancelCreate} disabled={creating}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={creating}
                disabled={!newForm.name.trim()}
                onClick={handleCreate}
              >
                Add Well
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export { ManageWellsModal };
